<script setup lang="ts">
import scrollIntoView from 'scroll-into-view-if-needed'

interface PersonOccurrenceEntry {
  id: string
  title: string
  dateStart: string
  dateEnd: string
  type: 'participant' | 'tutor' | 'staff'
  person?: { id: string; slug: string; title: string; sortValue?: string }
}

interface GuestPerson {
  id: string
  firstName: string
  lastName: string
  isExternal: boolean
  person?: { id: string; slug: string; title: string; sortValue?: string }
}

interface GuestDoc {
  id: string
  title: string
  dateStart: string
  dateEnd: string
  people: GuestPerson[]
}

interface ArchiveEntry {
  id: string
  title: string
  dateStart: string
  dateEnd: string
  kind: 'occurrence' | 'guest'
  type: 'participant' | 'tutor' | 'staff' | 'guest'
  personSlug?: string
  personName?: string
  sortValue: string
  yearLabel: string
}

interface TypeGroup {
  id: string
  yearLabel: string
  type: 'participant' | 'tutor' | 'staff' | 'guest'
  isFirstOfYear: boolean
  entries: ArchiveEntry[]
}

const props = defineProps<{
  entries: PersonOccurrenceEntry[]
  guests: GuestDoc[]
}>()

const typeOrder = ['participant', 'tutor', 'staff', 'guest'] as const

const flatGroups = computed<TypeGroup[]>(() => {
  const occurrences: ArchiveEntry[] = props.entries.map((o: PersonOccurrenceEntry) => ({
    id: o.id,
    title: o.title,
    dateStart: o.dateStart,
    dateEnd: o.dateEnd,
    kind: 'occurrence' as const,
    type: o.type,
    personSlug: o.person?.slug,
    sortValue: o.person?.sortValue ?? o.title,
    yearLabel: getFormattedYearRange(o.dateStart, o.dateEnd),
  }))

  const guestEntries: ArchiveEntry[] = props.guests.flatMap((g: GuestDoc) =>
    g.people.map((gp: GuestPerson) => {
      const personName = gp.person?.title ?? `${gp.firstName} ${gp.lastName}`.trim()
      return {
        id: `${g.id}-${gp.id}`,
        title: g.title,
        dateStart: g.dateStart,
        dateEnd: g.dateEnd,
        kind: 'guest' as const,
        type: 'guest' as const,
        personSlug: gp.person?.slug,
        personName,
        sortValue: gp.person?.sortValue ?? personName,
        yearLabel: getFormattedYearRange(g.dateStart, g.dateEnd),
      }
    })
  )

  const all = [...occurrences, ...guestEntries]

  // one group per year + type combination, flattened into a single list
  const groupMap = new Map<string, { yearLabel: string; type: ArchiveEntry['type']; dateStart: string; entries: ArchiveEntry[] }>()
  for (const entry of all) {
    const key = `${entry.yearLabel}-${entry.type}`
    if (!groupMap.has(key)) {
      groupMap.set(key, { yearLabel: entry.yearLabel, type: entry.type, dateStart: entry.dateStart, entries: [] })
    }
    const group = groupMap.get(key)!
    if (new Date(entry.dateStart).getTime() > new Date(group.dateStart).getTime()) group.dateStart = entry.dateStart
    group.entries.push(entry)
  }

  // keep the groups of one year adjacent (newest year first), ordered by type within the year
  const yearStart = new Map<string, number>()
  for (const group of groupMap.values()) {
    const time = new Date(group.dateStart).getTime()
    const current = yearStart.get(group.yearLabel)
    if (current === undefined || time > current) yearStart.set(group.yearLabel, time)
  }

  const sortedGroups = [...groupMap.entries()].sort(([, a], [, b]) => {
    const yearDiff = yearStart.get(b.yearLabel)! - yearStart.get(a.yearLabel)!
    if (yearDiff !== 0) return yearDiff
    return typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type)
  })

  const seenYears = new Set<string>()
  return sortedGroups.map(([key, group]) => {
    const isFirstOfYear = !seenYears.has(group.yearLabel)
    seenYears.add(group.yearLabel)
    return {
      id: key,
      yearLabel: group.yearLabel,
      type: group.type,
      isFirstOfYear,
      entries: sortBySortValue(group.entries, e => e.sortValue),
    }
  })
})

const yearLabels = computed(() =>
  flatGroups.value.filter(g => g.isFirstOfYear).map(g => g.yearLabel)
)

const yearRefs = ref<Record<string, HTMLElement>>({})

function setYearRef(yearLabel: string, el: Element | ComponentPublicInstance | null) {
  if (el instanceof HTMLElement) yearRefs.value[yearLabel] = el
}

function scrollToYear(yearLabel: string) {
  const el = yearRefs.value[yearLabel]
  if (!el) return

  const header = document.querySelector('header')
  el.style.scrollMarginTop = `${(header?.offsetHeight ?? 0) + 16}px`

  scrollIntoView(el, { behavior: 'smooth', block: 'start' })
}

defineExpose({ yearLabels, scrollToYear })
</script>

<template>
  <div
    class="grid grid-cols-4  <lg:grid-cols-3 <sm:grid-cols-1 gap-x-1 gap-y-2"
  >
    <div
      v-for="group in flatGroups"
      :key="group.id"
      :ref="el => group.isFirstOfYear ? setYearRef(group.yearLabel, el) : undefined"
    >
      <h2
        class="text-d col-span-1"
      >
        {{ $t(`pages.people.${group.type}`, { count: group.entries.length }) }}
      </h2>
    
      <h3 class="text-d mb-1">
        {{ group.yearLabel }}
      </h3>

      <ul>
        <li v-for="entry in group.entries" :key="entry.id">
          <shared-person-link :name="entry.personName ?? entry.title" :slug="entry.personSlug" />
        </li>
      </ul>
    </div>
  </div>
</template>
